/**
 * Lift2Fit — coaching workspace: To-do (`#/crm/todo`).
 *
 * The coach's task queue. It is deliberately a LIST OF TASKS, not a review
 * query: every row is the generic `CrmTask` envelope (`GET /crm/tasks`), and the
 * only producer today is the derived review signal, so every row is
 * `kind: 'review'`. A second kind arrives as another task in the same array and
 * renders through the same envelope — no branch in the list, no second panel.
 *
 * The review rows are NOT recomputed here. They come from
 * `apps/api/src/programme/block.ts` via the API's single review derivation, so
 * the To-do tab, the roster badge and the client page can never disagree about
 * when a block is due.
 */
import { useState } from 'react';
import { api, type CrmTask } from '@/lib/api';
import { AlertIcon, ArrowRightIcon, CheckIcon, TimerIcon } from '@/lib/icons';
import { EmptyState, LoadingPanel, Panel, ScreenHeader, StatusChip, type ChipTone } from '../components';
import { useAsync, useCachedAsync } from '../hooks';
import { Link } from '../router';
import { CRM_EYEBROW, ListMore, QUEUE_LIST_LIMIT, formatDateShort } from './shared';

/**
 * The one place a kind is turned into words and a tone. Adding a kind is one
 * entry here; the row below reads only the envelope and never switches on the
 * kind itself.
 */
function taskKindMeta(kind: string): { label: string; tone: ChipTone } {
  if (kind === 'review') return { label: 'Review due', tone: 'info' };
  return { label: 'Task', tone: 'neutral' };
}

/** Overdue is a status, so it wins the chip's word and colour together. */
function taskStatus(task: CrmTask): { label: string; tone: ChipTone } {
  if (task.overdue) return { label: 'Overdue', tone: 'danger' };
  return taskKindMeta(task.kind);
}

/** The kind-specific facts, read out of `meta` without trusting a fixed shape. */
function taskDetail(task: CrmTask): string {
  const parts: string[] = [];
  const blockName = typeof task.meta.blockName === 'string' ? task.meta.blockName : null;
  const weekOf = typeof task.meta.weekOf === 'number' ? task.meta.weekOf : null;
  const weeks = typeof task.meta.weeks === 'number' ? task.meta.weeks : null;
  if (blockName) parts.push(blockName);
  if (weekOf !== null && weeks !== null) parts.push(`week ${weekOf} of ${weeks}`);
  if (task.dueOn) parts.push(`ends ${formatDateShort(task.dueOn)}`);
  return parts.join(' · ') || 'Open the client to see what needs doing.';
}

/** Whole days left, or how far past the due date the task is. */
function taskTiming(task: CrmTask): string {
  if (task.daysRemaining === null) {
    return task.dueOn ? `due ${formatDateShort(task.dueOn)}` : 'no due date';
  }
  if (task.daysRemaining < 0) return `${Math.abs(task.daysRemaining)} day(s) overdue`;
  if (task.daysRemaining === 0) return 'due today';
  return `${task.daysRemaining} day(s) left`;
}

export function CrmTodoScreen() {
  const state = useCachedAsync('crm/tasks', () => api.crmTasks(), []);
  const [showAll, setShowAll] = useState(false);
  const tasks = state.data?.data ?? [];
  const total = state.data?.total ?? tasks.length;
  const overdue = state.data?.overdue ?? 0;
  // This queue is the destination the Summary's "View all" points at, so it can
  // reveal everything — but it starts bounded, with the honest total beside it,
  // rather than growing a taller and taller page as the book grows.
  const visible = showAll ? tasks : tasks.slice(0, QUEUE_LIST_LIMIT);

  return (
    <div className="flex flex-col gap-6" data-testid="crm-todo">
      <ScreenHeader
        eyebrow={CRM_EYEBROW}
        title="To-do"
        badge={
          state.data ? (
            <StatusChip tone={overdue ? 'danger' : total ? 'info' : 'neutral'}>
              {total} task(s)
            </StatusChip>
          ) : undefined
        }
        lead="What needs doing across your roster. Reviews come first: a client whose block is in its final week or already past its end. Other coaching tasks land in this same queue."
      />

      <Panel
        testId="crm-task-list"
        title="Task queue"
        subtitle="Soonest due first. Each row opens the client's programme so you can act on it."
        bodyClassName="gap-3"
      >
        {state.loading && !state.data ? (
          <LoadingPanel label="Loading your tasks" rows={3} />
        ) : state.error && !state.data ? (
          <EmptyState
            title="We could not read your tasks"
            body={state.error}
            icon={<AlertIcon className="h-8 w-8 text-accent" />}
          />
        ) : tasks.length ? (
          <>
            <ul className="flex flex-col divide-y divide-hairline" data-testid="crm-task-rows">
              {visible.map((task) => {
                const status = taskStatus(task);
                return (
                  <li key={task.id} data-testid={`crm-task-${task.id}`}>
                    <Link
                      to={task.href}
                      data-testid={`crm-task-link-${task.id}`}
                      className="flex flex-wrap items-center justify-between gap-3 py-3 hover:text-accent"
                    >
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="flex flex-wrap items-center gap-2 text-[14px] font-bold text-ink">
                          {task.title}
                          <StatusChip tone={status.tone} icon={<TimerIcon />}>
                            {status.label}
                          </StatusChip>
                          {task.clientName ? (
                            <span className="text-[12px] font-normal text-ink-muted">{task.clientName}</span>
                          ) : null}
                        </span>
                        <span className="text-[12px] text-ink-muted">{taskDetail(task)}</span>
                      </span>
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">
                          {taskTiming(task)}
                        </span>
                        <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-accent">
                          Open programme
                          <ArrowRightIcon />
                        </span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
            <ListMore
              total={total}
              shown={visible.length}
              onShowAll={() => setShowAll(true)}
              toLabel="Show all"
              testId="crm-task-more"
            />
          </>
        ) : (
          <div data-testid="crm-task-empty">
            <EmptyState
              title="Nothing to do"
              body="No coaching task is waiting right now. A client's block in its final week appears here for review, and other kinds land in this same queue as they arrive."
              icon={<CheckIcon className="h-8 w-8 text-success" />}
            />
          </div>
        )}
      </Panel>
    </div>
  );
}
